import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import * as React from 'react'
import {useMemo} from 'react'

import {useThrottle} from '../../../../../hooks/useThrottle'
import {getProject} from '../../../../../selectors'
import type {GetTestOccurrenceTreeApiArg} from '../../../../../services/rest'
import {restApi} from '../../../../../services/rest'
import type {ProjectId, NormalizedProject} from '../../../../../types'
import {subscribeOnProjectEvents} from '../../../../../utils/subscriber'
import {
  TEST_MUTE_UPDATED,
  TEST_RESPONSIBILITY_CHANGED,
} from '../../../../../utils/subscriptionEvents'
import TestsTree from '../../../../packages/Tests/TestsTree/TestsTree'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  labelId: string
  className?: string
  projectId: ProjectId
  locator: string
}
const requestTestOccurrenceTreeOptions = {
  withNewFailure: true,
  withMetadataCount: true,
  withNextFixed: true,
  withMuteInfo: true,
  withInvestigationInfo: true,
  withInvocationsCounters: true,
}

const THROTTLER_TIMEOUT = 5000

export const getOccurenceTreeArg = (locator: string): GetTestOccurrenceTreeApiArg => ({
  endpoint: 'testScopes/tree',
  locator,
  options: requestTestOccurrenceTreeOptions,
  treeLocator: locator,
})

function TestsTreeWrapper({labelId, projectId, locator, ...otherProps}: Props) {
  const project: Partial<NormalizedProject> =
    useAppSelector(state => getProject(state, projectId)) ?? {}
  const {internalId: projectInternalId} = project

  const arg = useMemo(() => getOccurenceTreeArg(locator), [locator])
  const {noLeaves, ready, refetch} = restApi.endpoints.getTestOccurrenceTree.useQuery(arg, {
    selectFromResult: ({data}) => ({
      noLeaves: data?.tree.leaf?.length === 0,
      ready: data != null,
    }),
  })
  const fetchTestOccurrenceTreeHandler = useThrottle(refetch, THROTTLER_TIMEOUT)

  React.useEffect(() => {
    if (projectInternalId != null) {
      return subscribeOnProjectEvents(
        projectInternalId,
        [TEST_RESPONSIBILITY_CHANGED, TEST_MUTE_UPDATED],
        fetchTestOccurrenceTreeHandler,
      )
    } else {
      fetchTestOccurrenceTreeHandler()
      return undefined
    }
  }, [projectInternalId, fetchTestOccurrenceTreeHandler])

  if (ready && noLeaves) {
    return null
  }
  return ready ? (
    <TestsTree
      labelId={labelId}
      locator={locator}
      projectId={projectId}
      arg={arg}
      {...otherProps}
    />
  ) : (
    <LoaderInline>{'Loading...'}</LoaderInline>
  )
}

export default React.memo(TestsTreeWrapper)
